import React, { useState, useEffect } from "react";
import { useAuth } from "@shadowhealth/oidc-login";
import { useParams, useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link as MuiLink,
  List,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";
import ConvoAskForm from "../components/ConvoAskForm";
import ConvoBrick from "../components/ConvoBrick";
import ConvoMatch from "../components/ConvoMatch";
import ConvoClarification from "../components/ConvoClarification";
import ConvoCompareResult from "../components/ConvoCompareResult";
import ConvoTrace from "../components/ConvoTrace";
import ParityGapsChip from "../components/ParityGapsChip";
import { enqueueSnackbar } from "notistack";
import callApi from "../helpers/callApi";

// Live single-question comparison, via the existing strict multi-preset
// /convo/compare route (not CWS-shaped, so it needs its own renderer — see
// ConvoCompareResult). The pair is author-selectable; the live preset leads so
// compare reads "what runs today" on the left.
const DEFAULT_COMPARE_PRESETS = ["convoengine_plus", "cws"];

// Preset names are the API's own values (and what docs/Jira use), so they are
// shown verbatim rather than relabelled per audience — only annotated.
const PresetSelect = ({ label, value, options, takenNames = [], onChange }) => {
  // aria-labelledby splits on whitespace, so a label like "Preset A" must not
  // go into the id verbatim or the control loses its accessible name.
  const labelId = label.toLowerCase().replaceAll(/\s+/g, "-");
  return (
    <FormControl size="small" sx={{ minWidth: 190 }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select labelId={labelId} label={label} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((preset) => (
          <MenuItem
            key={preset.name}
            value={preset.name}
            // /convo/compare rejects duplicate names, so the other slot's choice
            // can't be picked twice.
            disabled={takenNames.includes(preset.name)}
          >
            {preset.alias_of ? `${preset.name} (same as ${preset.alias_of})` : preset.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const ConvosPage = (params) => {
  const auth = useAuth();
  const { scriptName, scriptVersion } = useParams();
  const { explainEndpoint, scriptEndpoint } = params;
  const [convoResponses, setConvoResponses] = useState([]);
  const [question, setQuestion] = useState(null);
  const [phase, setPhase] = useState(null);
  const [phases, setPhases] = useState([]);
  const [rubricName, setRubricName] = useState(scriptName);
  const [rubricVersion, setRubricVersion] = useState("latest");
  const [modelQuestionTrigger, setModelQuestionTrigger] = useState(0);
  const [runningMQ, setRunningMQ] = useState(false);
  const [askTrigger, setAskTrigger] = useState(0);
  const [advancedInsights, setAdvancedInsights] = useState(false);
  // Ask and compare keep separate histories: interleaving a single answer with a
  // two-preset row in one feed made it unclear which preset produced what.
  const [compareResponses, setCompareResponses] = useState([]);
  const [askPreset, setAskPreset] = useState("");
  // The comparison lives in the URL — which tab and which two presets — so a
  // comparison can be linked to a colleague and reopened exactly as configured.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "compare" ? "compare" : "ask";
  const comparePresets = [
    searchParams.get("a") || DEFAULT_COMPARE_PRESETS[0],
    searchParams.get("b") || DEFAULT_COMPARE_PRESETS[1],
  ];

  const updateParams = (next) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => (value ? params.set(key, value) : params.delete(key)));
    // replace: switching tabs or presets is not a browsing step to go back through.
    setSearchParams(params, { replace: true });
  };

  const setTab = (next) => updateParams({ tab: next === "compare" ? "compare" : null });
  const setComparePreset = (slot, name) => updateParams(slot === 0 ? { a: name } : { b: name });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(globalThis.location.href);
      enqueueSnackbar("Comparison link copied", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(`Could not copy link: ${err.message}`, { variant: "error" });
    }
  };
  const [presetDefault, setPresetDefault] = useState("");
  // Preset names + their channels come from the API, so the picker can't drift
  // from what this build actually runs (and a classic preset can be labelled
  // from its channels rather than a hardcoded name list).
  const [presetOptions, setPresetOptions] = useState([]);

  const presetInfo = presetOptions.reduce((acc, preset) => ({ ...acc, [preset.name]: preset }), {});

  const renderCompareResponse = (response, idx) => (
    <ConvoCompareResult key={idx} response={response} presetInfo={presetInfo} expanded={advancedInsights} />
  );

  const renderResponse = (response, idx) => {
    let main;
    if (response.Answer.error) {
      main = <ConvoBrick response={response} />;
    } else if (response.Answer.matches.length > 1) {
      main = <ConvoClarification response={response} />;
    } else {
      main = <ConvoMatch response={response} />;
    }
    const gaps = presetInfo[response.__preset]?.known_gaps || [];
    return (
      <React.Fragment key={idx}>
        {main}
        {/* Which preset answered is never implicit — model-question rows are
            built client-side and carry no preset, so they show no stamp. */}
        {response.__preset && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 7, mb: 1, flexWrap: "wrap" }}>
            <Chip size="small" color="primary" label={response.__preset} />
            <ParityGapsChip gaps={gaps} />
          </Stack>
        )}
        {response.trace && <ConvoTrace trace={response.trace} expanded={advancedInsights} />}
      </React.Fragment>
    );
  };

  const comparing = tab === "compare";
  const history = comparing ? compareResponses : convoResponses;

  const downloadReport = () => {
    const blob = new Blob([JSON.stringify(history, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `convo-${comparing ? "compare" : "ask"}-report-${scriptName}-v${scriptVersion}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const runModelQuestions = () => {
    setModelQuestionTrigger(modelQuestionTrigger + 1);
  };

  const handleNewQuestion = (question, phase) => {
    setQuestion(question);
    setPhase(phase ?? null);
    // Bump a per-submit counter so re-asking the *same* text still fires
    // (e.g. asking once without tracing, then again with it). Keying the
    // effect on the question value alone makes identical re-submits no-ops.
    setAskTrigger((trigger) => trigger + 1);
  };

  const getConvoResponse = async () => {
    const basePayload = {
      character: scriptName,
      version: scriptVersion,
      question: question,
      // Only sent for phased scripts (ConvoAskForm supplies it). The backend
      // requires it for phased scripts and ignores it for unphased ones.
      ...(phase ? { phase } : {}),
    };
    try {
      if (comparing) {
        const response = await callApi(
          "/convo/compare",
          "POST",
          { ...basePayload, presets: comparePresets },
          auth.user?.access_token,
        );
        // /convo/compare doesn't echo the question back, and its shape isn't
        // CWS-shaped like /convo/explain — tag it so renderResponse/downloadReport
        // can tell the two apart.
        setCompareResponses((prevArray) => [{ ...response, question }, ...prevArray]);
      } else {
        const response = await callApi(
          // Always explain: every answer carries its trace; the UI just keeps the
          // technical detail collapsed. /convo/legacy stays the byte-compatible
          // contract for external callers, but this authoring view wants the why.
          `${explainEndpoint}`,
          "POST",
          // /convo/explain honours a preset (unlike /convo/legacy, which is
          // locked to CE+ channels), so the author can chat with any preset.
          { ...basePayload, ...(askPreset ? { preset: askPreset } : {}) },
          auth.user?.access_token,
        );
        setConvoResponses((prevArray) => [{ ...response, __preset: askPreset || presetDefault }, ...prevArray]);
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  const getScriptVersion = async () => {
    try {
      const response = await callApi(
        `${scriptEndpoint}/${scriptName}/${scriptVersion}`,
        "GET",
        undefined,
        auth.user?.access_token,
      );
      if (response?.rubric_name) {
        setRubricName(response.rubric_name);
      }
      if (response?.rubric_version) {
        setRubricVersion(response.rubric_version);
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  const getPresets = async () => {
    try {
      const response = await callApi("/convo/presets", "GET", undefined, auth.user?.access_token);
      const options = Array.isArray(response?.presets) ? response.presets : [];
      setPresetOptions(options);
      setPresetDefault(response.default || "");
      // Chatting defaults to the preset /convo/legacy serves, so the authoring
      // view matches what a learner gets until the author changes it.
      setAskPreset((current) => current || response.default || options[0]?.name || "");
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  const getScriptPhases = async () => {
    try {
      const response = await callApi(
        `${scriptEndpoint}/${scriptName}/${scriptVersion}/phases`,
        "GET",
        undefined,
        auth.user?.access_token,
      );
      setPhases(Array.isArray(response?.phases) ? response.phases : []);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  const modelQuestionToConvoResponse = (mq) => {
    if (mq?.question && mq?.error) {
      if (mq.error === "Bricked") {
        return {
          Answer: {
            error: `Model question bricked: ${mq.question}`,
          },
        };
      } else {
        return {
          Answer: {
            error: `Model question ${mq.question} has error ${mq.error}`,
          },
        };
      }
    } else {
      return {
        Answer: {
          matches: [
            {
              character_speech: mq,
              user_speech: "Model question test",
              meaning: "",
              is_exact: true,
              scores: [1],
            },
          ],
        },
      };
    }
  };

  const getModelQuestionResponse = async () => {
    try {
      setRunningMQ(true);
      const response = await callApi(
        `${scriptEndpoint}/${scriptName}/${scriptVersion}/test`,
        "GET",
        undefined,
        auth.user?.access_token,
      );
      const mqAsConvoResponses = Array.isArray(response?.models)
        ? response.models.map((mq) => modelQuestionToConvoResponse(mq))
        : [modelQuestionToConvoResponse(response.models)];
      setConvoResponses((prevArray) => [...mqAsConvoResponses, ...prevArray]);
      setRunningMQ(false);
    } catch (err) {
      setRunningMQ(false);
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  useEffect(() => {
    if (!!question && question?.length > 2) {
      getConvoResponse();
    }
  }, [askTrigger]);

  useEffect(() => {
    getScriptVersion();
    getScriptPhases();
  }, [scriptName, scriptVersion]);

  useEffect(() => {
    getPresets();
  }, []);

  useEffect(() => {
    if (presetOptions.length === 0) return;
    const names = presetOptions.map((preset) => preset.name);
    const [a, b] = comparePresets;
    if (names.includes(a) && names.includes(b) && a !== b) return;
    // A stale or hand-edited link would 400 on /convo/compare, so fall back to
    // this build's default plus the first preset that differs from it.
    const first = names.includes(presetDefault) ? presetDefault : names[0];
    updateParams({ a: first, b: names.find((name) => name !== first) });
  }, [presetOptions]);

  useEffect(() => {
    if (modelQuestionTrigger > 0) {
      getModelQuestionResponse();
    }
  }, [modelQuestionTrigger]);

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack spacing={1}>
            <Breadcrumbs aria-label="breadcrumb">
              <MuiLink component={RouterLink} underline="hover" color="inherit" to="/scripts">
                Scripts
              </MuiLink>
              <MuiLink component={RouterLink} underline="hover" color="inherit" to={`/scripts/${scriptName}`}>
                {scriptName}
              </MuiLink>
              <Typography color="text.primary">v{scriptVersion}</Typography>
            </Breadcrumbs>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Typography variant="h5" component="h1">
                {scriptName}: version {scriptVersion}
              </Typography>
              {phases.length > 1 && (
                <Chip size="small" color="secondary" label={`Multi-phase script · ${phases.length} phases`} />
              )}
            </Stack>
            <Typography variant="subtitle1">
              Rubric: {rubricName} / {rubricVersion}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Stack direction="column" spacing={3}>
            <Tabs value={tab} onChange={(event, next) => setTab(next)} sx={{ minHeight: 40 }}>
              <Tab value="ask" label="Ask" />
              <Tab value="compare" label="Compare presets" />
            </Tabs>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
              {/* Which preset answers is a first-class choice on both tabs: one
                  preset for chatting, two for a side-by-side. */}
              {comparing ? (
                comparePresets.map((selected, slot) => (
                  <PresetSelect
                    key={slot}
                    label={slot === 0 ? "Preset A" : "Preset B"}
                    value={selected}
                    options={presetOptions}
                    takenNames={comparePresets.filter((name, idx) => idx !== slot)}
                    onChange={(next) => setComparePreset(slot, next)}
                  />
                ))
              ) : (
                <PresetSelect label="Preset" value={askPreset} options={presetOptions} onChange={setAskPreset} />
              )}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={advancedInsights}
                    onChange={(event) => setAdvancedInsights(event.target.checked)}
                  />
                }
                label="Advanced insights"
              />
            </Stack>
            <ConvoAskForm onSubmit={handleNewQuestion} phases={phases} />
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
              {!comparing && (
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  disabled={runningMQ}
                  startIcon={runningMQ ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                  onClick={runModelQuestions}
                >
                  {runningMQ ? "Running model questions…" : "Test model questions"}
                </Button>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {comparing && (
                <Button size="small" variant="text" color="inherit" startIcon={<LinkIcon />} onClick={copyLink}>
                  Copy link
                </Button>
              )}
              <Button
                size="small"
                variant="text"
                color="inherit"
                disabled={history.length === 0}
                startIcon={<DownloadIcon />}
                onClick={downloadReport}
              >
                Download report
              </Button>
            </Stack>
            <List
              sx={{ width: "100%" }}
              aria-labelledby="list-subheader"
              component="div"
              disablePadding
              subheader={
                <ListSubheader component="div" id="list-subheader" sx={{ bgcolor: "transparent", px: 0 }}>
                  {comparing ? "Comparison history" : "Convo history"}
                </ListSubheader>
              }
            >
              {history.map((cr, i) => (comparing ? renderCompareResponse(cr, i) : renderResponse(cr, i)))}
            </List>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ConvosPage;
